import { Card, CardContent } from "@/components/ui/card";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
  setCurrentHwProgress,
  setCurrentQuizProgress,
} from "../../../store/slices/storeSlice";
import QuestionImg from "../QuestionImg";
import { AssessmentMode, QuestionData } from "../typesAndEnums";

const QuestionMCQ = ({
  assessmentMode,
  assessmentId,
  question,
  isEnded,
  mustAnswerAll,
  hideCorrectAnswers,
}: {
  assessmentMode: string;
  assessmentId: number;
  question: QuestionData;
  isEnded: boolean;
  mustAnswerAll: boolean;
  hideCorrectAnswers: boolean;
}) => {
  const dispatch = useAppDispatch();
  const { currentQuizProgress, currentHwProgress } = useAppSelector(
    (state) => state.store
  );

  const currentAssessmentProgress =
    assessmentMode === AssessmentMode.QUIZ
      ? currentQuizProgress
      : currentHwProgress;

  const handleOnChange = async (e: any) => {
    const progress = await JSON.parse(
      localStorage.getItem(`${assessmentMode}_${assessmentId}_user_answers`) ??
        "{}"
    );
    progress[question.question_id] = e.target.value;
    localStorage.setItem(
      `${assessmentMode}_${assessmentId}_user_answers`,
      JSON.stringify(progress)
    );
    if (assessmentMode === AssessmentMode.QUIZ) {
      dispatch(
        setCurrentQuizProgress({
          [question.question_id]: e.target.value,
        })
      );
    } else {
      dispatch(
        setCurrentHwProgress({
          [question.question_id]: e.target.value,
        })
      );
    }
  };

  const isChecked = (question: QuestionData, answer: string) => {
    if (isEnded && question?.userAnswer === answer) return true;
    if (
      !isEnded &&
      currentAssessmentProgress?.[question?.question_id] === answer
    )
      return true;
    return false;
  };

  const calculateClassName = (radioAnswer: string) => {
    const answer = currentAssessmentProgress?.[question?.question_id];
    const correctAnswer = question?.correct_answer;

    const defaultClass =
      "w-full border border-slate-200 flex justify-start items-center gap-2 py-2 px-4 rounded-lg ";

    if (isEnded) {
      if (radioAnswer !== answer && radioAnswer !== correctAnswer)
        return defaultClass;
      if (radioAnswer !== answer && radioAnswer === correctAnswer && !hideCorrectAnswers)
        return (
          defaultClass + "bg-green-100 border border-green-400 text-green-700"
        );
      if (radioAnswer === answer && radioAnswer !== correctAnswer)
        return defaultClass + "bg-red-500 border border-red-500 text-white";
      if (radioAnswer === answer && radioAnswer === correctAnswer)
        return defaultClass + "bg-green-600 border border-green-600 text-white";
    } else {
      if (radioAnswer === answer) return defaultClass + "bg-slate-200";
      return defaultClass + "cursor-pointer";
    }

    return defaultClass;
  };

  return (
    <Card className="mb-6">
      <CardContent className="p-8">
        {/* Cover Image */}
        {question.img && (
          <div className="mb-6">
            <QuestionImg id={`question-${question.question_id}`} src={question.img} alt={question.title} />
          </div>
        )}

        {question.content ? (
          <div key={question.question_id} className="mb-6">
            {question.content.indexOf("\n") !== 1 ? (
              question.content.split("\n").map((p: string) => <h2>{p}</h2>)
            ) : (
              <h2 className="text-xl font-semibold mb-4">{question.content}</h2>
            )}
          </div>
        ) : (
          ""
        )}

        <div
          className="w-full flex flex-col gap-4"
          role="group"
          aria-label="Basic radio toggle button group"
        >
          {question.answers.map((answer) => (
            <label
              key={`label-${question.question_id}-${answer}`}
              className={calculateClassName(answer)}
              htmlFor={`btnradio-${question.question_id}-${answer}`}
            >
              <input
                key={`${question.question_id}-${answer}`}
                type="radio"
                className="hidden"
                checked={isChecked(question, answer)}
                name={String(question.question_id)}
                id={`btnradio-${question.question_id}-${answer}`}
                value={answer}
                autoComplete="off"
                required={mustAnswerAll}
                onChange={(e) => handleOnChange(e)}
                disabled={isEnded}
              />
              <span>{answer}</span>
            </label>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default QuestionMCQ;
